import * as qz from 'qz-tray';
import config from '../config/qz';

export const QzTray = function (options) {
    /**
     * @type {object}
     */
    this.options = options;

    /**
     * @type {boolean}
     */
    this.loading = false;

    /**
     * @type {boolean}
     */
    this.connected = false;

    /**
     * @type {boolean}
     */
    this.connecting = false;

    /**
     * @type {boolean}
     */
    this.failed = false;

    /**
     * @type {boolean}
     */
    this.disconnecting = false;

    /**
     * QZTray instance
     *
     * @type {*|{configs, printers, print, hid, security, serial, usb, websocket, api, version}}
     */
    this.qz = qz;

    /**
     * Returns true if QzTray is initialized, false otherwise
     *
     * @returns boolean
     */
    this.isQzInitialized = () => {
        return this.qz && this.qz.version && this.qz.websocket;
    };

    /**
     * Launch QzTray
     */
    this.launch = () => {
        if (! this.isQzInitialized()) {
            return Promise.reject('QZ has not been initialized');
        }

        window.location.assign('qz:launch');

        return this.connect();
    };

    /**
     * Connect to QzTray
     *
     * @returns {Promise|Promise<never>|Promise<void>|Promise<T>}
     */
    this.connect = () => {
        if (! this.isQzInitialized()) {
            return Promise.reject('QZ has not been initialized.');
        }

        if (this.loading || this.qz.websocket.isActive()) {
            console.warn('An active connection with QZ already exists.');

            return Promise.resolve()
                .then(this._connectResolve)
                .finally(this._connectFinally);
        }

        this.failed = false;
        this.connecting = true;

        return this.qz.websocket.connect(config.connect)
            .then(this._connectResolve)
            .catch(this._websocketError)
            .finally(this._connectFinally);
    };

    /**
     * Disconnect from QzTray
     *
     * @returns {Promise|Promise<never>|Promise<void>|Promise<T>}
     */
    this.disconnect = () => {
        if (! this.isQzInitialized()) {
            return Promise.reject('No has not been initialized.');
        }

        if (this.loading || ! this.qz.websocket.isActive()) {
            console.warn('No connection with QZ is active.');
            return Promise.resolve()
                .then(this._disconnectResolve)
                .finally(this._disconnectFinally);
        }

        return this.qz.websocket.disconnect()
            .then(this._disconnectResolve)
            .catch(this._websocketError)
            .finally(this._disconnectFinally);
    };

    /**
     * Get the QzTray version
     *
     * @returns {*|Promise<string|Error>}
     */
    this.getVersion = () => {
        if (! this.isQzInitialized() || this.loading || ! this.qz.websocket.isActive()) {
            return Promise.reject('No connection with QZ is active');
        }

        return this.qz.api.getVersion();
    };

    /**
     * @private
     */
    this._setupQzTray = () => {
        if (this.qz && this.qz.security) {
            if (this.options.certificatePromiseCallback) {
                this.qz.security.setCertificatePromise(this.options.certificatePromiseCallback);
            }

            if (this.options.signaturePromiseCallback) {
                this.qz.security.setSignaturePromise(this.options.signaturePromiseCallback);
            }
        }

        this.qz.websocket.setClosedCallbacks(this._websocketClosed);
        this.qz.websocket.setErrorCallbacks(this._websocketError);

        this.qz.api.setPromiseType(function promise(resolver) { return new Promise(resolver); });

        this.qz.api.setSha256Type(function (data) {
            let crypto = require('crypto');
            return crypto.createHash('sha256').update(data).digest('hex');
        });
    };

    /**
     * @private
     */
    this._connectResolve = () => {
        this.connected = true;
        this.failed = false;
    };

    /**
     * @private
     */
    this._connectFinally = () => {
        this.connecting = false;
    };

    /**
     * @private
     */
    this._disconnectResolve = () => {
        this.connected = false;
        this.failed = false;
    };

    /**
     * @private
     */
    this._disconnectFinally = () => {
        this.disconnecting = false;
    };

    /**
     * @param error
     * @private
     */
    this._websocketError = (error) => {
        this.failed = true;
        this.connected = false;
        this.connecting = false;
        this.disconnecting = false;

        console.error('Error:', error);
    };

    /**
     * @param closeEvent
     * @private
     */
    this._websocketClosed = (closeEvent) => {
        this.connected = false;
        this.connecting = false;
        this.disconnecting = false;

        if (closeEvent.reason) {
            console.warn('Connection closed:', closeEvent.reason);
        }
    };

    this._setupQzTray();

    return this;
};

export default QzTray;
